import type { TimelineEvent } from "@nexus/widgets";

/** What a dashboard can say it is currently about, beyond its own title. */
export type DashboardContextKind = "tenant" | "event" | "protected_event";

export interface DashboardContextEntry {
  kind: DashboardContextKind;
  value: string;
}

export const DASHBOARD_CONTEXT_KINDS: readonly DashboardContextKind[] = [
  "tenant",
  "event",
  "protected_event",
];

/** The captions shown before each value. */
export const DASHBOARD_CONTEXT_LABELS: Record<DashboardContextKind, string> = {
  tenant: "Tenant",
  event: "Event",
  protected_event: "Protected event",
};

/**
 * What the context line shows when the dashboard does not say. The event and its
 * protected event already sit in the header's own Event control, so repeating
 * them would only be noise; the tenant is the one part of the business context
 * no control displays (the Tenant selector is a global filter, not the tenant
 * the dashboard is about).
 */
export const DEFAULT_CONTEXT_SHOW: readonly DashboardContextKind[] = ["tenant"];

/**
 * Entries to display, in a fixed order (tenant › event › protected event):
 * `show` picks which kinds, `[]` hides the line, and a kind the context cannot
 * resolve (no protected event yet, say) is left out rather than shown empty.
 */
export function resolveContextEntries(
  available: readonly DashboardContextEntry[],
  show: readonly DashboardContextKind[] = DEFAULT_CONTEXT_SHOW,
): DashboardContextEntry[] {
  return DASHBOARD_CONTEXT_KINDS.flatMap((kind) => {
    if (!show.includes(kind)) return [];
    const entry = available.find((candidate) => candidate.kind === kind);
    return entry?.value ? [entry] : [];
  });
}

/**
 * What an event-oriented dashboard is about. The tenant is the one that owns
 * the selected protected event, else the tenant of the event when it has a
 * single one. An event spread over several tenants is not one tenant's, so it
 * names none — the comparison views read across all of them.
 */
export function eventContextEntries({
  eventLabel,
  event,
  protectedEventId,
  tenantLabels,
}: {
  eventLabel?: string | null;
  event?: Pick<TimelineEvent, "tenant_ids" | "protected_events"> | null;
  protectedEventId?: string | null;
  /** Tenant id → label, for tenants the event's own data does not name. */
  tenantLabels?: ReadonlyMap<string, string>;
}): DashboardContextEntry[] {
  const entries: DashboardContextEntry[] = [];
  const protectedEvent = protectedEventId
    ? event?.protected_events.find((candidate) => candidate.protected_event_id === protectedEventId)
    : undefined;
  const tenantIds = [...new Set(event?.tenant_ids ?? [])];
  const tenantId = protectedEvent?.tenant_id ?? (tenantIds.length === 1 ? tenantIds[0] : undefined);
  const tenant =
    protectedEvent?.tenant_label ||
    (tenantId
      ? (event?.protected_events.find((candidate) => candidate.tenant_id === tenantId)
          ?.tenant_label ?? tenantLabels?.get(tenantId))
      : undefined);
  if (tenant) entries.push({ kind: "tenant", value: tenant });
  if (eventLabel) entries.push({ kind: "event", value: eventLabel });
  if (protectedEvent?.channel)
    entries.push({ kind: "protected_event", value: protectedEvent.channel });
  return entries;
}
